import * as React from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAtom } from 'jotai'
import type { UserDocument } from '@hooks/use-ocr.hooks'
import { filesAtom } from '@store/atoms'
import {
	currentSideAtom,
	documentModeAtom,
	documentSubStepIndexAtom,
} from '@ui/richiesta/stepper/data/store'
//import DocumentUpload from './DocumentUpload'

import DocumentUpload from './pages/contacts/flow/DocumentUpload'
import WizardFooter from './pages/thank-you/WizardFooter'
import Header from './Header'
import {
	useApi,
	type APIParams,
	type APIRequest,
} from '@hooks/use-api-requests.hook'
import MobileMultiFileUploadPage from './MobileMultiFileUploadPage'
import Loader from '@ui/components/loader'
import CustomRadioGroup from '@ui/components/CustomRadioGroup'
import check from '@assets/Ico_check_conferma.png'
import { useAppContext } from '@hooks/use-app-context'
import { isDeviceMobile } from '@utils/mobile'

const ALLOWED_DOCS: UserDocument[] = ['IDE', 'CF', 'ALTRO']

const DOC_TO_SUBSTEP: Record<UserDocument, number> = {
	IDE: 1,
	CF: 2,
	ALTRO: 3,
	X: 4,
}

const mappedOptions = [
	{ id: 1, value: 'MOBILE', label: 'Procedi su questo dispositivo' },
	{ id: 2, value: 'DESKTOP', label: 'Procedi dal computer' },
]

export default function MobileMissingDocumentsUploadPage() {
	const { state, dispatch } = useAppContext()
	const navigate = useNavigate()
	const location = useLocation()
	const { token } = useParams<{ token?: string }>()

	const docId = React.useMemo(() => {
		const params = new URLSearchParams(location.search)
		const raw = params.get('docId')
		return ALLOWED_DOCS.includes(raw as UserDocument)
			? (raw as UserDocument)
			: null
	}, [location.search])

	const subStepId = docId ? DOC_TO_SUBSTEP[docId] : null

	const isMultiFileDocument = docId === 'ALTRO'

	const [mode, setMode] = useAtom(documentModeAtom)
	const [currentSide, setCurrentSide] = useAtom(currentSideAtom)
	const [subStepIndex, setSubStepIndex] = useAtom(documentSubStepIndexAtom)
	const [filesMap] = useAtom(filesAtom)

	const submitRef = React.useRef<(() => void) | null>(null)
	const saveRef = React.useRef<(() => void) | null>(null)

	////--------------------------------------------
	const [phase, setPhase] = React.useState<'UPLOAD' | 'CHOICE'>('UPLOAD')
	const [choice, setChoice] = React.useState<'MOBILE' | 'DESKTOP' | null>(null)
	const [busy, setBusy] = React.useState(false)
	const [error, setError] = React.useState<string | null>(null)

	/////STEP LABEL
	const [multiStepLabel, setMultiStepLabel] = React.useState('Anteprima del documento')

	const closeConfig = React.useMemo<APIParams<'MOBILE' | 'DESKTOP'>>(
		() => ({
			request: 'DbOperations/closeMobileSession' as APIRequest,
			method: 'GET',
			silent: true,
			sleeper: true,
			shouldCache: false,
		}),
		[]
	)

	const [, closeSession] = useApi<'MOBILE' | 'DESKTOP'>(closeConfig)

	const reviewConfig = React.useMemo<APIParams<boolean>>(
		() => ({
			request: 'DbOperations/reviewMobileSession' as APIRequest,
			method: 'GET',
			silent: true,
			sleeper: true,
			shouldCache: false,
		}),
		[]
	)
	const [, triggerReviewRequest] = useApi<boolean>(reviewConfig)

	const onUploadComplete = () => {
		setBusy(false)
		setPhase('CHOICE')
	}

	const onConfirmChoice = async () => {
		setBusy(true)
		setError(null)
		try {
			if (choice === 'MOBILE') {
				await closeSession({
					query: { idMobileSession: token, source: 'MOBILE' },
				} as any)

				if (token) {
					sessionStorage.setItem('mobileSessionToken', token)
					sessionStorage.setItem(
						'idRichiestaStorage',
						String(state.data.IdRichiesta)
					)
				}

				navigate('/richiesta/missingdocuments', { replace: true })
			} else {
				await triggerReviewRequest({
					query: { idMobileSession: token },
				} as any)
				navigate('/Home/MobileSwitch?mobile=true', { replace: true })
			}
		} catch (e: any) {
			setError(e?.message ?? 'Errore nella conferma')
		} finally {
			setBusy(false)
		}
	}

	///----------------------------------------

	React.useEffect(() => {
		if (!subStepId) return
		setSubStepIndex(0)
		setCurrentSide('front')
		setMode('insert')
	}, [subStepId, setSubStepIndex, setCurrentSide, setMode])

	if (!token) return <div className='p-4'>Token mancante</div>
	if (!docId || !subStepId)
		return <div className='p-4'>Documento non valido</div>

	const substepFiles = filesMap[subStepId] ?? { front: null, back: null }
	const hasFront = !!substepFiles.front
	const hasBack = !!substepFiles.back

	const [mobileMultiNextDisabled, setMobileMultiNextDisabled] =
		React.useState(true)

	const nextDisabled = React.useMemo(() => {
		if (phase === 'CHOICE') return busy || !choice

		if (isMultiFileDocument) return mobileMultiNextDisabled

		if (subStepIndex === 0 && mode === 'insert') return !hasFront
		if (subStepIndex === 0 && mode === 'preview') return false

		if (subStepIndex === 1 && mode === 'insert') return !hasBack
		if (subStepIndex === 1 && mode === 'preview') return false

		if (subStepIndex === 2) return false

		return false
	}, [
		phase,
		choice,
		busy,
		isMultiFileDocument,
		mobileMultiNextDisabled,
		subStepIndex,
		mode,
		hasFront,
		hasBack,
	])

	/* const nextDisabled = React.useMemo(() => {
		if (subStepIndex === 0 && mode === 'insert') return !hasFront
		if (subStepIndex === 0 && mode === 'preview') return false

		if (subStepIndex === 1 && mode === 'insert') return !hasBack
		if (subStepIndex === 1 && mode === 'preview') return false

		if (subStepIndex === 2) return false

		return false
	}, [subStepIndex, mode, hasFront, hasBack]) */

	console.log(
		'mode',
		mode,
		'phase',
		phase,
		'ismulti',
		isMultiFileDocument,
		'frpont',
		hasFront,
		'index',
		subStepIndex,
		'back',
		hasBack,
		'id',
		subStepId,
		multiStepLabel
	)
	const goNext = () => {
		if (phase === 'CHOICE') {
			void onConfirmChoice()
			return
		}

		if (isMultiFileDocument) {
			saveRef.current?.()
			return
		}

		if (subStepIndex === 0 && mode === 'insert') {
			if (!hasFront) return
			setMode('preview')
			return
		}

		if (subStepIndex === 0 && mode === 'preview') {
			setSubStepIndex(1)
			setCurrentSide('back')
			setMode('insert')
			return
		}

		if (subStepIndex === 1 && mode === 'insert') {
			if (!hasBack) return
			setMode('preview')
			return
		}

		if (subStepIndex === 1 && mode === 'preview') {
			setSubStepIndex(2)
			setMode('insert')
			return
		}

		if (subStepIndex === 2) {
			setBusy(true)
			submitRef.current?.()
		}
	}

	const goBack = () => {
		// In choice phase docs are already saved — back has no meaning
		if (phase === 'CHOICE') return

		if (isMultiFileDocument) {
			navigate('/richiesta/missingdocuments', { replace: true })
			return
		}

		if (subStepIndex === 0 && mode === 'preview') {
			setMode('insert')
			return
		}

		if (subStepIndex === 1 && mode === 'insert') {
			setSubStepIndex(0)
			setCurrentSide('front')
			setMode('preview')
			return
		}

		if (subStepIndex === 1 && mode === 'preview') {
			setMode('insert')
			return
		}

		if (subStepIndex === 2) {
			setSubStepIndex(1)
			setCurrentSide('back')
			setMode('preview')
			return
		}
	}

	const nextStepLabel = React.useMemo(() => {
	
		if (isMultiFileDocument) {
			return multiStepLabel
		}

		if (hasFront && subStepIndex === 0) {
			return isDeviceMobile()
				? 'Fotografa o allega il retro del documento'
				: 'Allega il retro del documento'
		}

		if (hasBack && subStepIndex === 0) {
			return isDeviceMobile()
				? 'Fotografa o allega il fronte del documento'
				: 'Allega il fronte del documento'
		}

		if (hasFront && hasBack && subStepIndex <2) {
			return 'Le immagini sono pronte'
		}

		if (subStepIndex === 2) {
			return ''
		}
		
		return 'Anteprima del documento'
	}, [isMultiFileDocument,multiStepLabel, subStepIndex, hasFront, hasBack])

	return (
		<>
			<div
				className='w-full
            min-h-screen
            flex flex-col
            bg-[linear-gradient(180deg,rgba(248,249,250,0)_17.59%,rgba(216,233,195,0.5)_135.26%)]
            '
			>
				<Header />
				{phase === 'CHOICE' ? (
					<main className='flex-1'>
						<div className='w-full max-w-[1296px] mx-auto pt-5 md:pt-14 px-3 md:px-6 flex flex-col gap-4'>
							<Loader isOpen={busy} />
							<h2 className='text-(--primary-blue) text-[28px] md:text-[40px] font-semibold'>
								Il caricamento dei documenti è andato a buon fine!
							</h2>
							<div className='flex flex-col items-center justify-center pb-10 gap-3.5'>
								<img src={check} alt='' className='w-14 h-14' />

								<p className='font-medium text-center'>
									Per completare la richiesta scegli se riprendere dal computer
									o procedere su questo dispositivo.
								</p>
							</div>
							{error && (
								<div className='mt-3 text-(--text-red-alert)'>{error}</div>
							)}
							<CustomRadioGroup
								name='mobileChoice'
								options={mappedOptions.map((o) => ({
									value: o.value,
									label: o.label,
								}))}
								selectedValue={choice ?? ''}
								minH='min-h-[80px]'
								colsDesktop='md:grid-cols-2'
								onChange={(val) => setChoice(val as 'MOBILE' | 'DESKTOP')}
							/>
						</div>
					</main>
				) : (
					<main className='flex-1'>
						{/* Wrapper centrale */}
						<div
							className='w-full
                            max-w-[1296px]
                            mx-auto 
                            pt-5 md:pt-14 
                            px-3 md:px-6
                            flex flex-col
                            gap-2
                            md:gap-4'
						>
							<div className='md:pb-8 pt-2.5 md:pt-0'>
								<Loader isOpen={busy} />

								{isMultiFileDocument ? (
									<MobileMultiFileUploadPage
										docId={docId}
										subStepId={subStepId}
										registerSave={(fn) => {
											saveRef.current = fn
										}}
										onDone={() => {
											onUploadComplete()
										}}
										setNextDisabled={setMobileMultiNextDisabled}
										onStepLabelChange={setMultiStepLabel}
									/>
								) : (
									<DocumentUpload
										subStepId={subStepId}
										docId={docId}
										registerSubmit={(fn) => {
											submitRef.current = fn
										}}
										registerSave={(fn) => {
											saveRef.current = fn
										}}
										onSaveSuccess={() => {
											onUploadComplete()
										}}
										onRetryOcr={() => {
											saveRef.current?.()
										}}
									/>
								)}
								{/* <DocumentUpload
								subStepId={subStepId}
								docId={docId}
								registerSubmit={(fn) => {
									submitRef.current = fn
								}}
								registerSave={(fn) => {
									saveRef.current = fn
								}}
								 onSaveSuccess={() => {
        void onSaveMissingDocument()
      }}
								onRetryOcr={() => {
									saveRef.current?.()
								}}
							/> */}
							</div>
						</div>
					</main>
				)}
				<WizardFooter
					nextStepLabel={nextStepLabel}
					onBack={phase === 'CHOICE' ? () => {} : goBack}
					onNext={goNext}
					nextDisabled={nextDisabled}
					nextLabel={
						phase === 'CHOICE'
							? 'Conferma'
							: subStepIndex === 2
								? 'Invia'
								: 'Avanti'
					}
				/>
			</div>
		</>
	)
}
